/**
 * Logout safety: before signing out (which wipes the device), try to send everything that is still
 * queued — calendar writes (outbox.ts) and annotation ops (annotation engine) — and report how many
 * changes would be lost.
 */
import { flushOutbox, pendingOutboxCount } from './outbox';

const FLUSH_TIMEOUT_MS = 20_000;

function withTimeout(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    p.then(
      () => {
        clearTimeout(t);
        resolve();
      },
      () => {
        clearTimeout(t);
        resolve();
      },
    );
  });
}

async function annotationsModule() {
  try {
    return await import('../annotations/useSheetAnnotations');
  } catch {
    return null; // chunk failed to load (offline on an old shell): counted as unknown → 0
  }
}

/** Number of unsent changes of this user right now (no network). */
export async function countPendingChanges(userId: string): Promise<number> {
  const ann = await annotationsModule();
  const [calendar, annotations] = await Promise.all([pendingOutboxCount(userId), ann ? ann.pendingAnnotationOps(userId).catch(() => 0) : Promise.resolve(0)]);
  return calendar + annotations;
}

/** Tries to send everything (bounded wait), then returns how many changes are still unsent. */
export async function flushPendingChanges(userId: string): Promise<number> {
  if (navigator.onLine) {
    const ann = await annotationsModule();
    await withTimeout(Promise.all([flushOutbox(), ann ? ann.flushAnnotationOps() : Promise.resolve()]), FLUSH_TIMEOUT_MS);
  }
  return countPendingChanges(userId);
}

/** Thrown by logout() when changes are still unsent (see api/auth.ts). */
export class PendingChangesError extends Error {
  constructor(public readonly count: number) {
    super(`${count} pending changes`);
    this.name = 'PendingChangesError';
  }
}
